import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import {ErrorCode, ErrorResult, ErrorSource, ErrorType, UnauthorizedBody } from '../dtos/error-message.dto';
import { AuthenticationProvider, domainsToAuthenticationMap } from './domains-to-authentication-map';
import analyticsService from './analytics.service';
import { EventType } from '../dtos/analytics.dto';
import {CUSTOMER_SERVICE_ERROR_SOURCE, STUB_ID_KEY, STUB_ID_QUERY_PARAM} from '../utils/constants';

type AxiosConfigType = Partial<AxiosRequestConfig>;
class AxiosService {

    private readonly ajaxRequest: Map<string, AbortController> = new Map<string, AbortController>();

    constructor() {
        axios.interceptors.response.use(
            this.handleSuccess,
            this.handleError
        );
        axios.defaults.headers.common['X-XSRF-TOKEN'] = this.getCookie('XSRF-TOKEN');
    }

    private getCookie(name: string) {
        const cookieArray = document.cookie.split(';');
        for (const cookie of cookieArray) {
            let newCookie = cookie;
            while (newCookie.startsWith(' ')) {
                newCookie = newCookie.substring(1);
            }
            if (newCookie.startsWith(name + '=')) {
                return newCookie.substring(name.length + 1, newCookie.length);
            }
        }
        return '';
    }

    private readonly getHeaders = (config?: AxiosConfigType): any => {
        return {
            'X-ORIGINAL-URL': window.location.href,
            ...config?.headers
        };
    };

    private readonly handleSuccess = (response: AxiosResponse): AxiosResponse => {
        return response;
    };

    public getAuthenticationProvider = () => {
        const currentDomain = window.location.hostname;
        return domainsToAuthenticationMap[currentDomain];
    };

    private readonly redirectToAuthentication = () => {
        window.location.href = this.buildAuthenticationUrl();
    };

    private readonly buildAuthenticationUrl = (): string => {
        const authenticationProvider = this.getAuthenticationProvider();
        return `/api/custom/login/oauth2/authorization/${authenticationProvider}${this.consumeStubIdQuery()}`;
    };

    // Premier stubId is single-use: it is consumed here and handed to the gateway on the login redirect.
    private readonly consumeStubIdQuery = (): string => {
        const stubId = sessionStorage.getItem(STUB_ID_KEY);
        if (!stubId) {
            return '';
        }
        sessionStorage.removeItem(STUB_ID_KEY);
        return `?${STUB_ID_QUERY_PARAM}=${encodeURIComponent(stubId)}`;
    };

    public redirectToIdentityProviderLogout = (response: AxiosError) => {
        window.location.href = response.response!.headers['location'];
    };

    private readonly redirectToUnauthorized = (body: any) => {
        let errorType: ErrorType;
        const provider = this.getAuthenticationProvider();
        switch (body) {
            case UnauthorizedBody.NO_MATCHING_ROLE:
            case UnauthorizedBody.NO_ROLES:
            case UnauthorizedBody.NO_UBANS:
                errorType = AuthenticationProvider.AAD === provider ? ErrorType.AAD : ErrorType.HALOB_NO_ROLES;
                break;
            case UnauthorizedBody.NO_MATCHING_TRIPLETS:
            case UnauthorizedBody.NO_ACCOUNT:
            case UnauthorizedBody.NO_ORGANIZATIONS:
                errorType = ErrorType.HALOB_NO_TRIPLETS;
                break;
            case '':
                errorType = AuthenticationProvider.AAD === provider ? ErrorType.AAD : ErrorType.HALOB_NO_TRIPLETS;
                break;
            default:
                errorType = ErrorType.HALOB_NO_TRIPLETS;
        }
        analyticsService.sendEvent(EventType.UnAuthorized, undefined, undefined, undefined, {},
            () => { window.location.href = `/unauthorized/${errorType}`; });
    };

    private readonly handleError = (error: AxiosError) => {
        if (error.response?.status === ErrorCode.REDIRECT_TO_LOGOUT) {
            this.redirectToIdentityProviderLogout(error);
        }
        if (error.response?.status === ErrorCode.UNAUTHENTICATED) {
            analyticsService.sendEvent(EventType.UnAuthenticated, undefined, undefined, undefined, {}, this.redirectToAuthentication);
        } else if (error.response?.status === ErrorCode.UNAUTHORIZED) {
            this.redirectToUnauthorized(error.response.data);
        } else if (error.response?.status === ErrorCode.REDIRECT) {
            this.redirectHome();
        } else if (error.response?.status === ErrorCode.NOT_FOUND) {
            this.handleNotFound(error);
        }
        return Promise.reject(error);
    };

    private readonly handleNotFound = (error: AxiosError) => {
        const errorData = error.response?.data;
        try {
            if( errorData &&
                (
                    (typeof errorData === 'object' && (errorData as ErrorResult).errorSource === ErrorSource.CUSTOMER_SERVICE) ||
                    (typeof errorData === 'string' && errorData.includes(CUSTOMER_SERVICE_ERROR_SOURCE))
                )
            ) {
                this.redirectHome();
            }
        } catch (error) {
            console.error(`Failed to parse 404 response error message`, error);
        }
    };

    private readonly redirectHome = () => {
        this.cancelAllReqs();
        window.location.href = '/';
    };

    private cancelPrevReqs(path: string): AbortSignal | undefined {
        // cancel  previous ajax if exists
        const pathKey = path.split('?')[0];
        this.ajaxRequest.get(pathKey)?.abort();

        // creates a new token for upcoming ajax (overwrite the previous one)
        this.ajaxRequest.set(pathKey, new AbortController());
        return this.ajaxRequest.get(pathKey)?.signal;
    }

    private cancelAllReqs() {
        this.ajaxRequest.forEach(req => req.abort());
    }

    private readonly send = async (innerMethod: (...args: any[]) => Promise<any>, path: string, payload?: any, isSignal = true, config?: AxiosConfigType) => {
        return innerMethod(path, payload, isSignal, config);
    };

    public post = async (path: string, payload?: any, isSignal = true, config?: AxiosConfigType) => {
        return this.send(this.innerPost, path, payload, isSignal, config);
    };

    public get = async (path: string, isSignal = true, config?: AxiosConfigType) => {
        return this.send(this.innerGet, path, null, isSignal, config);
    };

    public put = async (path: string, payload: any, isSignal = true, config?: AxiosConfigType) => {
        return this.send(this.innerPut, path, payload, isSignal, config);
    };

    public delete = async (path: string, payload?: any, isSignal = true, config?: AxiosConfigType) => {
        return this.send(this.innerDelete, path, payload, isSignal, config);
    };

    private readonly innerGet = async (path: string, payload: any = null, isSignal = true, config?: AxiosConfigType) => {
        return axios.get(path, {
            ...config,
            signal: isSignal ? this.cancelPrevReqs(path) : undefined,
            headers: this.getHeaders()
        });
    };

    private readonly innerPut = async (path: string, payload: any, isSignal = true, config?: AxiosConfigType) => {
        return axios.put(path, payload, {
            ...config,
            signal: isSignal ? this.cancelPrevReqs(path) : undefined,
            headers: this.getHeaders(),
            withCredentials: true
        });
    };

    private readonly innerPost = async (path: string, payload?: any, isSignal = true, config?: AxiosConfigType) => {
        return axios.post(path, payload, {
            ...config,
            signal: isSignal ? this.cancelPrevReqs(path) : undefined,
            headers: this.getHeaders(config),
            withCredentials: true
        });
    };

    private readonly innerDelete = async (path: string, payload?: any, isSignal = true, config?: AxiosConfigType) => {
        return axios.delete(path, {
            ...config,
            signal: isSignal ? this.cancelPrevReqs(path) : undefined,
            data: payload,
            headers: this.getHeaders(),
            withCredentials: true
        });
    };
}

const axiosService = new AxiosService();
export default axiosService;
